<script>
/**
 * @desc
 *  Component in charged of displaying Nanobar.<br>
 *  Intercepts the client HTTP requests and responses.<br>
 *  Use store/Nanobar module. The global progressCounter is used to manage multi requests callings.
 */
import { isUndefined } from 'lodash-es'
import Nanobar from 'nanobar'
import eventBus from '@/modules/eventBus'

export default {
  data () {
    return {
      // Nanobar instance
      nanobar: new Nanobar(),
      // number of starts
      counter: 0,
      // Boolean used to save the start Nanobar status in order to avoid multiple Nanobar start callings.
      started: false,
      // Nanobar status
      progress: null,
      // Timeouts and intervals
      timeoutStop: null,
      intervalProgress: null
    }
  },

  watch: {
    /**
     * @desc
     *  Start the progress bar when the counter switch from 0 to another value.<br>
     *  Stop the progress bar when the counter equals 0.
     */
    counter: function (value) {
      if (value === 0) {
        this.stopLoading()
        this.started = false
      } else if (!this.started) {
        this.started = true
        this.startLoading()
      }
    }
  },

  created () {
    // start events
    eventBus.$on('request-start', this.startProgress)
    eventBus.$on('request-async-start', this.startProgress)
    eventBus.$on('progress-start', this.startProgress)

    // stop events
    eventBus.$on('request-succeed', this.stopProgress)
    eventBus.$on('request-failed', this.stopProgress)
    eventBus.$on('request-async-succeed', this.stopProgress)
    eventBus.$on('request-async-failed', this.stopProgress)
    eventBus.$on('progress-stop', this.stopProgress)

    // reset events
    eventBus.$on('progress-reset', this.resetProgress)
  },

  destroyed () {
    // start events
    eventBus.$off('request-start', this.startProgress)
    eventBus.$off('request-async-start', this.startProgress)
    eventBus.$off('progress-start', this.startProgress)

    // stop events
    eventBus.$off('request-succeed', this.stopProgress)
    eventBus.$off('request-failed', this.stopProgress)
    eventBus.$off('request-async-succeed', this.stopProgress)
    eventBus.$off('request-async-failed', this.stopProgress)
    eventBus.$off('progress-stop', this.stopProgress)

    // reset events
    eventBus.$off('progress-reset', this.resetProgress)
  },

  methods: {
    startProgress (method, url, data, headers, options) {
      if (
        isUndefined(options) ||
        isUndefined(options.loader) || options.loader
      ) {
        this.counter++
      }
    },

    stopProgress (response) {
      if (
        (
          isUndefined(response) ||
          isUndefined(response.config) ||
          isUndefined(response.config.options) ||
          isUndefined(response.config.options.loader) || response.config.options.loader
        ) &&
        this.counter > 0
      ) {
        this.counter--
      }
    },

    resetProgress () {
      this.counter = 0
    },

    /**
    * @desc Start progress bar.
    */
    startLoading () {
      clearTimeout(this.timeoutStop)
      const initialProgress = 0
      this.progress = this.progress || initialProgress

      if (this.progress === initialProgress) {
        this.nanobar.go(this.progress)
        const stepMillis = 100
        const nbStep = 30000 / stepMillis
        this.intervalProgress = setInterval(() => {
          this.progress = this.progress + (100 - this.progress) / (nbStep / 2)
          this.nanobar.go(this.progress)
        }, stepMillis)
      }
    },

    /**
    * @desc End progress bar.
    */
    stopLoading () {
      this.progress = this.progress + Math.min(5, (100 - this.progress) / 200)
      clearTimeout(this.timeoutStop)

      this.timeoutStop = setTimeout(() => {
        if (this.progress > 0) {
          clearInterval(this.intervalProgress)
          this.nanobar.go(100)
          this.progress = null
        }
      }, 500)
    },

    /**
    * @desc Reset the progress bar.
    */
    resetLoading () {
      clearTimeout(this.timeoutStop)
      clearInterval(this.intervalProgress)
      this.progress = null
      this.nanobar.go(0)
    }
  },

  render () {
    return null
  }
}
</script>

<style lang="scss">
  /* Not scoped as the real nanobar is outside this component */
  @import "~@/scss/variables";

  .nanobar {
    top: 0;
    z-index: 9999;
    width: 100%;
    height: 2px;

    /* Make clicks pass-through */
    pointer-events: none;

    .bar {
      position: relative;
      width: 0;
      height: 100%;
      background: $secondary;
      transition: height 0.3s;

      /* Fancy blur effect */
      &::after {
        position: absolute;
        right: 0;
        display: block;
        width: 100px;
        height: 100%;
        content: '';
        box-shadow: 0 0 10px $secondary, 0 0 5px $secondary;
        opacity: 1;
        -webkit-transform: rotate(3deg) translate(0, -4px);
        -ms-transform: rotate(3deg) translate(0, -4px);
        transform: rotate(3deg) translate(0, -4px);
      }
    }
  }

  @media (min-width: $grid-breakpoint-md) {
    .nanobar {
      .bar {
        background: $primary;

        /* Fancy blur effect */
        &::after {
          box-shadow: 0 0 10px $primary, 0 0 5px $primary;
        }
      }
    }
  }
</style>
